import Phaser from "phaser";
import i18next from "i18next";
import { WindowManager } from "../../../ui/window-manager";
import { BaseEquipmentWindow } from "../windows/base-equipment-window";
import { BaseInventoryWindow } from "../windows/base-inventory-window";
import { StorageWindow, findAmmoDefForWeapon } from "../windows/storage-window";
import { ModernButton } from "../../../ui/modern-button";
import { NavButton } from "../../../ui/nav-button";
import { EquipType, Item, ItemDefinition, ItemType, SquadMemberDetail, SquadUpdateData, getArmorItemsFromEquipment, getHatSpriteKey, getItemStat } from "../../../models/common";
import { ArmorShieldBar } from "../../../ui/armor-shield-bar";
import { SquadPartnerDisplay } from "../../../ui/squad-partner-display";
import { LoadoutWindow } from "../../../ui/windows/loadout-window";
import { playSound } from "../../../utils/sound-utils";
import { COLORS } from "../../../ui/colors";
import { DailyChallengeClientData } from "../../../models/daily-challenge";
import { fetchItemDefs } from "../../../item-catalog";

const IDLE_ANIM_KEY = "deploy_idle";
// Hat bobs down this many pixels (1px scaled by the preview sprite's 3.2x scale) on the
// second idle frame, in sync with the 2-frame body animation, then back up.
const HAT_BOB_OFFSET = 1 * 3.2;
const DURATION_OPEN = 300;
const DURATION_CLOSE = 220;
const OFF_SCREEN_LEFT = -400;
const EQUIP_CONTENT_W = 140;
const INV_CONTENT_W = 240;
const WINDOW_GAP = 16;

export class DeployScreen {
  private readonly scene: Phaser.Scene;
  private readonly panel: Phaser.GameObjects.Container;
  private readonly windowManager: WindowManager;
  private readonly equipmentWindow: BaseEquipmentWindow;
  private readonly inventoryWindow: BaseInventoryWindow;
  private readonly storageWindow: StorageWindow;
  private readonly pedestal: Phaser.GameObjects.Graphics;
  private readonly pedestalShadow: Phaser.GameObjects.Ellipse;
  private readonly playerSprite: Phaser.GameObjects.Sprite;
  private readonly weaponSprite: Phaser.GameObjects.Sprite;
  private readonly hatSprite: Phaser.GameObjects.Sprite;
  private readonly hatBaseY: number;
  private readonly armorBar: ArmorShieldBar;
  private readonly backButton: ModernButton;
  private readonly confirmButton: NavButton;
  private readonly readyButton: NavButton;
  private readonly reloadAllButton: ModernButton;
  private readonly reloadAllCostText: Phaser.GameObjects.Text;
  private itemDefsCache: ItemDefinition[] = [];
  private readonly squadPartnerDisplay: SquadPartnerDisplay;
  private squadCode: string | null = null;
  private memberDetails: SquadMemberDetail[] = [];
  private myAccountId: number | undefined;
  private isLeader: boolean = true;
  private readonly playerX: number;
  private readonly pedestalY: number;
  private readonly baseX: number;
  private readonly invBaseX: number;
  private readonly baseY: number;
  private readonly storageHiddenX: number;
  private readonly backButtonBaseX: number;
  private readonly onBack: () => void;
  private readonly onDeployConfirm: () => void;
  private readonly loadoutWindow: LoadoutWindow;
  private playerNameText: Phaser.GameObjects.Text | null = null;
  private playerNameBg: Phaser.GameObjects.Graphics | null = null;
  private dailyChallengeBoxObjects: Phaser.GameObjects.GameObject[] = [];
  private dailyChallengeDataListener: ((data: DailyChallengeClientData | null) => void) | null = null;
  private storageOpen: boolean = false;
  private storageOpenMode: "equipment" | "inventory" | null = null;
  private selectedEquipType: EquipType | null = null;

  constructor(
    scene: Phaser.Scene,
    windowManager: WindowManager,
    panel: Phaser.GameObjects.Container,
    uiLayer: Phaser.GameObjects.Container,
    onBack: () => void,
    onDeployConfirm: () => void,
  ) {
    this.scene = scene;
    this.panel = panel;
    this.windowManager = windowManager;
    this.onBack = onBack;
    this.onDeployConfirm = onDeployConfirm;
    this.loadoutWindow = new LoadoutWindow(scene, uiLayer);

    const { width, height } = scene.scale;

    this.playerX = Math.floor(width / 2);
    this.pedestalY = Math.floor(height * 0.48);
    this.baseX = Math.floor(width / 4 - 128);
    this.invBaseX = Math.floor(width * 3 / 4 - 128);
    this.baseY = -48;
    this.storageHiddenX = width + 200;
    this.backButtonBaseX = width / 2 - 64;

    this.squadPartnerDisplay = new SquadPartnerDisplay(scene, panel, this.playerX, this.pedestalY, "deploy");

    this.pedestalShadow = scene.add.ellipse(
      this.playerX,
      this.pedestalY + 24,
      178,
      38,
      0x000000,
      0.35,
    );

    this.pedestal = scene.add.graphics();
    this.pedestal.fillStyle(0x141a28, 1);
    this.pedestal.fillEllipse(this.playerX, this.pedestalY + 10, 164, 36);
    this.pedestal.fillStyle(0x2f4f88, 1);
    this.pedestal.fillEllipse(this.playerX, this.pedestalY + 4, 150, 26);
    this.pedestal.lineStyle(3, 0x8cb7ef, 1);
    this.pedestal.strokeEllipse(this.playerX, this.pedestalY + 4, 150, 26);

    this.playerSprite = scene.add.sprite(
      this.playerX,
      this.pedestalY - 42,
      "player",
      0,
    );
    this.playerSprite.setScale(3.2);

    this.weaponSprite = scene.add.sprite(
      this.playerX,
      this.pedestalY - 22,
      "weapon",
      0,
    );
    this.weaponSprite.setOrigin(0, 0.5);
    this.weaponSprite.setScale(2.4);
    this.weaponSprite.setVisible(false);

    // Player sprite is centered at pedestalY - 42 with a 32px frame at scale 3.2, so its top
    // edge (the head) sits at pedestalY - 42 - 16 * 3.2 = pedestalY - 93.2. Nudged down 32px so
    // the hat rests on the head instead of floating above it.
    this.hatBaseY = this.pedestalY - 93.2 + 42;
    this.hatSprite = scene.add.sprite(
      this.playerX,
      this.hatBaseY,
      "hat-1",
      0,
    );
    this.hatSprite.setOrigin(0.5, 1);
    this.hatSprite.setScale(3.2);
    this.hatSprite.setVisible(false);

    // Bob the hat with the body's 2-frame idle animation
    this.playerSprite.on(
      Phaser.Animations.Events.ANIMATION_UPDATE,
      (
        _animation: Phaser.Animations.Animation,
        frame: Phaser.Animations.AnimationFrame,
      ) => {
        this.hatSprite.y = this.hatBaseY + (frame.index === 2 ? HAT_BOB_OFFSET : 0);
      },
    );

    this.armorBar = new ArmorShieldBar(
      scene,
      this.playerX - 40,
      this.pedestalY - 113,
      true,
      2,
      true,
    );
    panel.add(this.armorBar.bg);

    const btnY = height - 60;
    this.backButton = new ModernButton({
      scene,
      x: width / 2 - 64,
      y: btnY + 8,
      text: i18next.t("ui:buttons.back"),
      style: "secondary",
      onClick: () => this.handleBackClick(),
    });

    this.confirmButton = new NavButton({
      scene,
      x: width / 2 + 64,
      y: btnY,
      text: i18next.t("ui:buttons.play"),
      style: "success",
      width: 135,
      onClick: () => this.handleDeployClick(),
    });

    this.readyButton = new NavButton({
      scene,
      x: width / 2 + 64,
      y: btnY,
      text: i18next.t("ui:buttons.setReady"),
      style: "default",
      width: 135,
      onClick: () => this.handleReadyClick(),
    });

    this.reloadAllButton = new ModernButton({
      scene,
      x: this.playerX,
      y: this.pedestalY + 70,
      text: "RELOAD ALL",
      style: "warning",
      onClick: () => this.handleReloadAllClick(),
    });

    this.reloadAllCostText = scene.add.text(
      this.playerX,
      this.pedestalY + 96,
      "",
      { fontSize: "14px", color: "#000000", fontFamily: "Anton" },
    );
    this.reloadAllCostText.setOrigin(0.5, 0);

    panel.add([
      this.pedestalShadow,
      this.pedestal,
      ...this.squadPartnerDisplay.getDisplayObjects(),
      this.playerSprite,
      this.weaponSprite,
      this.hatSprite,
      this.reloadAllButton,
      this.reloadAllCostText,
      this.backButton,
      this.confirmButton,
      this.readyButton,
    ]);

    this.pedestalShadow.setVisible(false);
    this.pedestal.setVisible(false);
    this.playerSprite.setVisible(false);
    this.reloadAllButton.setVisible(false);
    this.reloadAllCostText.setVisible(false);
    this.backButton.setVisible(false);
    this.confirmButton.setVisible(false);
    this.readyButton.setVisible(false);

    fetchItemDefs().then((defs) => {
      this.itemDefsCache = defs;
      if (this.playerSprite.visible) this.updateReloadAllButton();
    });

    this.equipmentWindow = new BaseEquipmentWindow(scene);
    this.equipmentWindow.setOnSlotSelectionChange((selected, equipType) => {
      if (selected) {
        this.openStorage("equipment", equipType);
      } else {
        this.closeStorage();
      }
    });
    this.inventoryWindow = new BaseInventoryWindow(scene, windowManager);

    this.storageWindow = new StorageWindow(scene);
    this.storageWindow.setLayoutMode("compact");
    this.storageWindow.setShowSellButton(false);
    this.storageWindow.setShowEquipButton(false);
    this.storageWindow.setShowItemTypeButtons(false);
    this.storageWindow.setShowCredits(false);

    (window as any).socket?.off("player-data", this.onPlayerData);
    (window as any).socket?.on("player-data", this.onPlayerData);
  }

  private readonly onPlayerData = (): void => {
    if (this.playerSprite.visible) {
      this.syncPlayerDisplay();
      this.updateReloadAllButton();
    }
    // Update player name in case it arrived after initial render
    if (this.playerNameText) {
      this.playerNameText.setText((window as any).player?.data?.name || "Guest");
    }
  };

  public destroy(): void {
    (window as any).socket?.off("player-data", this.onPlayerData);
    this.removeDailyChallengeBox();
  }

  private handleDeployClick(): void {
    if (this.hasWeaponWithAmmo()) {
      this.proceedToPlay();
    } else {
      this.showLoadoutWindow(() => this.proceedToPlay());
    }
  }

  private proceedToPlay(): void {
    playSound(this.scene, "teleport");
    if (this.squadPartnerDisplay.getOtherMember()) {
      // Full squad - the server is the single authority on whether we can proceed
      // (blocks with a notification if the partner is mid-match or not ready yet);
      // navigation itself happens via the squad-join-lobby listener in BaseScene.
      this.scene.time.delayedCall(300, () => {
        (window as any).socket?.emit("squad-teleport", { requireReady: true });
      });
    } else {
      this.scene.time.delayedCall(300, () => this.onDeployConfirm());
    }
  }

  private handleReadyClick(): void {
    if (this.hasWeaponWithAmmo()) {
      this.toggleReady();
    } else {
      this.showLoadoutWindow(() => this.toggleReady());
    }
  }

  private toggleReady(): void {
    const myDetail = this.memberDetails.find((m) => m.accountId === this.myAccountId);
    const nextReady = !(myDetail?.ready ?? false);
    (window as any).socket?.emit("squad-set-ready", { ready: nextReady });
  }

  private hasWeaponWithAmmo(): boolean {
    const equipment = (window as any).player?.data?.equipment as Partial<Record<string, Item>> | undefined;
    const weapon = equipment?.[EquipType.Weapon];
    if (!weapon) return false;
    return weapon.currentAmmo === undefined || weapon.currentAmmo > 0;
  }

  /**
   * Total credit cost (and count) to top up every under-ammo weapon across equipment,
   * inventory, AND the action bar — a weapon can sit in any of the three, and the
   * action bar's own ammo-dot indicator (ItemUI.renderAmmoIndicator) uses the same
   * "has an ammo stat" criterion rather than requiring EquipType.Weapon specifically.
   */
  private computeReloadAllCost(): { totalCost: number; weaponCount: number } {
    const equipment = (window as any).player?.data?.equipment as Partial<Record<EquipType, Item>> | undefined;
    const inventory = (window as any).player?.data?.inventory as Item[] | undefined;
    const actions = (window as any).player?.data?.actions as Item[] | undefined;
    const candidates = [
      ...(equipment ? Object.values(equipment) : []),
      ...(inventory ?? []),
      ...(actions ?? []),
    ].filter((item): item is Item => !!item);

    let totalCost = 0;
    let weaponCount = 0;
    for (const item of candidates) {
      const maxAmmo = getItemStat(item, "ammo");
      if (!maxAmmo) continue;
      const currentAmmo = item.currentAmmo ?? maxAmmo;
      if (currentAmmo >= maxAmmo) continue;
      const ammoDef = findAmmoDefForWeapon(item.definition.id, this.itemDefsCache);
      if (!ammoDef) continue;
      totalCost += Math.ceil(ammoDef.price * (1 - currentAmmo / maxAmmo));
      weaponCount++;
    }
    return { totalCost, weaponCount };
  }

  private updateReloadAllButton(): void {
    const { totalCost, weaponCount } = this.computeReloadAllCost();
    if (weaponCount === 0) {
      this.reloadAllButton.setVisible(false);
      this.reloadAllCostText.setVisible(false);
      return;
    }
    const credits = (window as any).player?.data?.credits ?? 0;
    const shown = this.playerSprite.visible && !this.storageOpen;
    this.reloadAllButton.setDisabled(credits < totalCost);
    this.reloadAllButton.setVisible(shown);
    this.reloadAllCostText.setText(`${totalCost} CREDITS`);
    this.reloadAllCostText.setVisible(shown);
  }

  private handleReloadAllClick(): void {
    (window as any).socket?.emit("reload-all-weapons");
  }

  private showLoadoutWindow(onProceed: () => void): void {
    const socket = (window as any).socket;
    if (!socket) return;
    socket.once("loadout-items", (items: ItemDefinition[]) => {
      this.loadoutWindow.show(items, {
        onTake: () => {
          socket.emit("take-loadout");
          onProceed();
        },
        onSkip: () => onProceed(),
      });
    });
    socket.emit("request-loadout");
  }

  private handleBackClick(): void {
    if (this.storageOpen) {
      const mode = this.storageOpenMode;
      this.closeStorage();
      if (mode === "equipment") {
        this.equipmentWindow.clearSelection();
      } else if (mode === "inventory") {
        this.inventoryWindow.clearSelection();
      }
    } else {
      this.onBack();
    }
  }

  private syncArmorBar(): void {
    const equipment = (window as any).player?.data?.equipment;
    if (!equipment) { this.armorBar.hide(); return; }
    const items = getArmorItemsFromEquipment(equipment);
    if (items.length > 0) {
      this.armorBar.setArmorItems(items);
      this.armorBar.show();
    } else {
      this.armorBar.hide();
    }
  }

  private syncPlayerDisplay(): void {
    const activeSkin = (window as any).player?.data?.activeSkin as string | undefined;
    const desiredTexture = activeSkin || "player";
    const textureKey = this.scene.textures.exists(desiredTexture) ? desiredTexture : "player";

    if (this.scene.anims.exists(IDLE_ANIM_KEY)) {
      this.scene.anims.remove(IDLE_ANIM_KEY);
    }
    this.scene.anims.create({
      key: IDLE_ANIM_KEY,
      frames: this.scene.anims.generateFrameNumbers(textureKey, { start: 0, end: 1 }),
      frameRate: 4,
      repeat: -1,
    });

    this.playerSprite.setTexture(textureKey, 0);
    this.playerSprite.setFlipX(false);
    this.playerSprite.play(IDLE_ANIM_KEY, true);

    const weapon = (window as any).player?.data?.equipment?.Weapon as Item | undefined;
    if (weapon && weapon.definition?.spriteSheet === "weapon") {
      const frame = weapon.definition.spriteY * 10 + weapon.definition.spriteX;
      this.weaponSprite.setFrame(frame);
      this.weaponSprite.setVisible(true);
    } else {
      this.weaponSprite.setVisible(false);
    }

    const equippedHat = (window as any).player?.data?.equippedHat as number | undefined;
    const hatTextureKey = getHatSpriteKey(equippedHat);
    if (hatTextureKey && this.scene.textures.exists(hatTextureKey)) {
      this.hatSprite.setTexture(hatTextureKey);
      this.hatSprite.setVisible(true);
    } else {
      this.hatSprite.setVisible(false);
    }

    this.syncArmorBar();
  }

  private openStorage(mode: "equipment" | "inventory", equipType?: EquipType): void {
    if (this.storageOpen && this.storageOpenMode === mode) {
      if (mode === "equipment" && equipType && equipType !== this.selectedEquipType) {
        this.selectedEquipType = equipType;
        (this.storageWindow as any).updateFilteredEquipType(equipType);
      }
      return;
    }
    this.storageOpen = true;
    this.storageOpenMode = mode;
    this.selectedEquipType = mode === "equipment" ? (equipType ?? null) : null;

    // Hide DEPLOY/SET READY, center BACK
    this.confirmButton.setVisible(false);
    this.readyButton.setVisible(false);
    this.reloadAllButton.setVisible(false);
    this.reloadAllCostText.setVisible(false);
    this.squadPartnerDisplay.setScreenVisible(false);
    this.scene.tweens.killTweensOf(this.backButton);
    this.scene.tweens.add({ targets: this.backButton, x: this.scene.scale.width / 2, duration: DURATION_OPEN, ease: "Cubic.Out" });

    // Hide player
    this.armorBar.hide();
    this.removePlayerNameDisplay();
    this.scene.tweens.killTweensOf([this.playerSprite, this.weaponSprite, this.pedestalShadow, this.pedestal]);
    this.scene.tweens.add({ targets: this.playerSprite, x: OFF_SCREEN_LEFT, duration: DURATION_OPEN, ease: "Cubic.Out" });
    this.scene.tweens.add({ targets: this.weaponSprite, x: OFF_SCREEN_LEFT, duration: DURATION_OPEN, ease: "Cubic.Out" });
    this.scene.tweens.add({ targets: this.pedestalShadow, x: OFF_SCREEN_LEFT, duration: DURATION_OPEN, ease: "Cubic.Out" });
    this.scene.tweens.add({ targets: this.pedestal, x: OFF_SCREEN_LEFT - this.playerX, duration: DURATION_OPEN, ease: "Cubic.Out" });

    // Selected window shifts minimally; other window slides off-screen left
    const equipContainer = (this.equipmentWindow as any).container as Phaser.GameObjects.Container | null;
    const invContainer = (this.inventoryWindow as any).container as Phaser.GameObjects.Container | null;
    if (equipContainer) {
      this.scene.tweens.killTweensOf(equipContainer);
      this.scene.tweens.add({
        targets: equipContainer,
        x: mode === "equipment" ? this.baseX : OFF_SCREEN_LEFT,
        duration: DURATION_OPEN,
        ease: "Cubic.Out",
      });
    }
    if (invContainer) {
      this.scene.tweens.killTweensOf(invContainer);
      this.scene.tweens.add({
        targets: invContainer,
        x: mode === "inventory" ? this.baseX - 48 : OFF_SCREEN_LEFT,
        duration: DURATION_OPEN,
        ease: "Cubic.Out",
      });
    }

    // Storage slides in from the right, placed after the selected window's content
    const selectedX = mode === "inventory" ? this.baseX - 48 : this.baseX;
    const contentW = mode === "inventory" ? INV_CONTENT_W : EQUIP_CONTENT_W;
    const storageTargetX = selectedX + contentW + WINDOW_GAP;
    if (mode === "equipment" && equipType) {
      this.storageWindow.setFilteredEquipType(equipType);
      this.storageWindow.setShowEquipButton(true);
      this.storageWindow.setShowTakeButton(false);
      this.storageWindow.setShowItemTypeButtons(false);
      this.storageWindow.setHiddenItemTypes([]);
    } else {
      this.storageWindow.setFilteredEquipType(null);
      this.storageWindow.setShowEquipButton(false);
      this.storageWindow.setShowTakeButton(mode === "inventory");
      this.storageWindow.setShowItemTypeButtons(mode === "inventory");
      // Material items can't be taken out to inventory from the loadout screen
      this.storageWindow.setHiddenItemTypes(mode === "inventory" ? [ItemType.Material] : []);
    }
    this.storageWindow.setScreenContainer(this.panel);
    this.storageWindow.show(true);
    this.storageWindow.setScrollMaskOffset(storageTargetX, this.baseY);
    const storageContainer = (this.storageWindow as any).container as Phaser.GameObjects.Container | null;
    if (storageContainer) {
      storageContainer.setPosition(this.storageHiddenX, this.baseY);
      this.scene.tweens.add({ targets: storageContainer, x: storageTargetX, duration: DURATION_OPEN, ease: "Cubic.Out" });
    }
  }

  private closeStorage(): void {
    if (!this.storageOpen) return;
    this.storageOpen = false;
    this.storageOpenMode = null;
    this.selectedEquipType = null;

    // Restore DEPLOY/SET READY, return BACK to original position
    this.refreshConfirmButtonMode();
    this.updateReloadAllButton();
    this.scene.tweens.killTweensOf(this.backButton);
    this.backButton.setX(this.backButtonBaseX);

    // Kill in-progress tweens
    this.scene.tweens.killTweensOf([this.playerSprite, this.weaponSprite, this.pedestalShadow, this.pedestal]);

    // Slide player display back to original position
    this.scene.tweens.add({
      targets: this.playerSprite,
      x: this.playerX,
      duration: DURATION_CLOSE,
      ease: "Cubic.In",
      onComplete: () => { this.syncArmorBar(); this.createPlayerNameDisplay(); this.squadPartnerDisplay.setScreenVisible(true); },
    });
    this.scene.tweens.add({
      targets: this.weaponSprite,
      x: this.playerX,
      duration: DURATION_CLOSE,
      ease: "Cubic.In",
    });
    this.scene.tweens.add({
      targets: this.pedestalShadow,
      x: this.playerX,
      duration: DURATION_CLOSE,
      ease: "Cubic.In",
    });
    this.scene.tweens.add({
      targets: this.pedestal,
      x: 0,
      duration: DURATION_CLOSE,
      ease: "Cubic.In",
    });

    const equipContainer = (this.equipmentWindow as any).container as Phaser.GameObjects.Container | null;
    if (equipContainer) {
      this.scene.tweens.killTweensOf(equipContainer);
      this.scene.tweens.add({ targets: equipContainer, x: this.baseX, duration: DURATION_CLOSE, ease: "Cubic.In" });
    }
    const invContainer = (this.inventoryWindow as any).container as Phaser.GameObjects.Container | null;
    if (invContainer) {
      this.scene.tweens.killTweensOf(invContainer);
      this.scene.tweens.add({ targets: invContainer, x: this.invBaseX, duration: DURATION_CLOSE, ease: "Cubic.In" });
    }

    const storageContainer = (this.storageWindow as any).container as Phaser.GameObjects.Container | null;
    if (storageContainer) {
      this.scene.tweens.killTweensOf(storageContainer);
      this.scene.tweens.add({
        targets: storageContainer,
        x: this.storageHiddenX,
        duration: DURATION_CLOSE,
        ease: "Cubic.In",
        onComplete: () => {
          this.storageWindow.hide();
        },
      });
    } else {
      this.storageWindow.hide();
    }
  }

  private createPlayerNameDisplay(): void {
    this.removePlayerNameDisplay();
    const playerName = (window as any).player?.data?.name || "Guest";
    this.playerNameText = this.scene.add.text(
      this.playerX,
      this.pedestalY - 140,
      playerName,
      { fontSize: "20px", color: COLORS.YELLOW, fontFamily: "Anton", align: "center" },
    );
    this.playerNameText.setOrigin(0.5, 0.5);
    this.playerNameText.setScrollFactor(0);

    const nameBgPadX = 14;
    const nameBgPadY = 8;
    this.playerNameBg = this.scene.add.graphics();
    this.playerNameBg.fillStyle(0x000000, 0.55);
    this.playerNameBg.fillRoundedRect(
      this.playerX - this.playerNameText.width / 2 - nameBgPadX,
      this.pedestalY - 140 - this.playerNameText.height / 2 - nameBgPadY,
      this.playerNameText.width + nameBgPadX * 2,
      this.playerNameText.height + nameBgPadY * 2,
      10,
    );
    this.playerNameBg.setScrollFactor(0);

    this.panel.add([this.playerNameBg, this.playerNameText]);
  }

  private removePlayerNameDisplay(): void {
    if (this.playerNameBg) {
      this.panel.remove(this.playerNameBg, true);
      this.playerNameBg = null;
    }
    if (this.playerNameText) {
      this.panel.remove(this.playerNameText, true);
      this.playerNameText = null;
    }
  }

  // ── Squad presentation ────────────────────────────────────────────────────

  updateSquadState(state: { squadCode: string | null; memberDetails: SquadMemberDetail[]; myAccountId?: number }): void {
    this.squadCode = state.squadCode;
    this.memberDetails = state.memberDetails;
    this.myAccountId = state.myAccountId;

    this.isLeader = this.memberDetails.length === 0 || this.memberDetails[0]?.accountId === this.myAccountId;

    this.squadPartnerDisplay.updateState(this.memberDetails, this.myAccountId);
    this.refreshConfirmButtonMode();
  }

  updateSquadMemberData(data: SquadUpdateData): void {
    this.squadPartnerDisplay.updateMemberData(data);
  }

  clearSquadState(): void {
    this.squadCode = null;
    this.memberDetails = [];
    this.isLeader = true;
    this.squadPartnerDisplay.clear();
    this.refreshConfirmButtonMode();
  }

  private refreshConfirmButtonMode(): void {
    const shown = this.playerSprite.visible && !this.storageOpen;
    const inFullSquad = !!this.squadPartnerDisplay.getOtherMember();

    if (!inFullSquad || this.isLeader) {
      this.readyButton.setVisible(false);
      this.confirmButton.setVisible(shown);
    } else {
      this.confirmButton.setVisible(false);
      this.readyButton.setVisible(shown);
      this.updateReadyButtonLabel();
    }
  }

  private updateReadyButtonLabel(): void {
    const myDetail = this.memberDetails.find((m) => m.accountId === this.myAccountId);
    const ready = myDetail?.ready ?? false;
    this.readyButton.setText(i18next.t(ready ? "ui:buttons.waiting" : "ui:buttons.setReady"));
    this.readyButton.setStyle(ready ? "success" : "default");
  }

  private fetchDailyChallenge(): void {
    this.removeDailyChallengeBox();
    const onData = (data: DailyChallengeClientData | null) => {
      this.dailyChallengeDataListener = null;
      if (data) this.createDailyChallengeBox(data);
    };
    this.dailyChallengeDataListener = onData;
    (window as any).socket?.once("daily-challenge-data", onData);
    (window as any).socket?.emit("get-daily-challenge");
  }

  private createDailyChallengeBox(data: DailyChallengeClientData): void {
    this.removeDailyChallengeBox();

    const { today } = data;
    const boxW = 180;
    const boxH = 64;
    const bx = 16;
    const by = this.scene.scale.height - 92;

    const borderColor = today.completedToday ? 0x4ade80 : 0x5b9bd5;
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0c1c38, 0.9);
    bg.fillRoundedRect(bx, by, boxW, boxH, 8);
    bg.lineStyle(2, borderColor, 1);
    bg.strokeRoundedRect(bx, by, boxW, boxH, 8);
    this.panel.add(bg);

    const label = this.scene.add.text(bx + 10, by + 7, `DAILY CHALLENGE · ${data.streakCount}/7`, {
      fontSize: "12px", color: "#7fd8f0", fontFamily: "Anton",
      stroke: "#0d1e2e", strokeThickness: 2,
    });
    this.panel.add(label);

    const challengeText = `Kill ${today.killTarget} ${today.monster}`;
    const challenge = this.scene.add.text(bx + 10, by + 24, challengeText, {
      fontSize: "14px", color: "#ffffff", fontFamily: "Anton",
      stroke: "#000000", strokeThickness: 3,
    });
    this.panel.add(challenge);

    if (today.completedToday) {
      const check = this.scene.add.text(bx + boxW - 10, by + boxH / 2, "✓", {
        fontSize: "22px", color: "#4ade80", fontFamily: "Anton",
        stroke: "#000000", strokeThickness: 3,
      }).setOrigin(1, 0.5);
      this.panel.add(check);
      this.dailyChallengeBoxObjects.push(check);
    }

    this.dailyChallengeBoxObjects.push(bg, label, challenge);
  }

  private removeDailyChallengeBox(): void {
    if (this.dailyChallengeDataListener) {
      (window as any).socket?.off("daily-challenge-data", this.dailyChallengeDataListener);
      this.dailyChallengeDataListener = null;
    }
    for (const obj of this.dailyChallengeBoxObjects) {
      this.panel.remove(obj, true);
    }
    this.dailyChallengeBoxObjects = [];
  }

  show(): void {
    this.loadoutWindow.hide();
    this.windowManager.hideAll();

    // Reset state and positions from any previous storage-open session
    this.storageOpen = false;
    this.storageOpenMode = null;
    this.scene.tweens.killTweensOf([this.playerSprite, this.weaponSprite, this.hatSprite, this.pedestalShadow, this.pedestal, this.backButton]);
    this.backButton.setX(this.backButtonBaseX);
    this.playerSprite.setX(this.playerX);
    this.weaponSprite.setX(this.playerX);
    this.hatSprite.setX(this.playerX);
    this.pedestalShadow.setX(this.playerX);
    this.pedestal.setPosition(0, 0);
    this.storageWindow.hide();

    this.syncPlayerDisplay();
    this.createPlayerNameDisplay();
    this.fetchDailyChallenge();

    this.pedestalShadow.setVisible(true);
    this.pedestal.setVisible(true);
    this.playerSprite.setVisible(true);
    this.backButton.setVisible(true);
    this.refreshConfirmButtonMode();
    this.updateReloadAllButton();
    this.squadPartnerDisplay.setScreenVisible(true);

    this.equipmentWindow.setScreenContainer(this.panel);
    this.equipmentWindow.show();
    const equipContainer = (this.equipmentWindow as any).container as Phaser.GameObjects.Container | null;
    if (equipContainer) {
      equipContainer.setPosition(this.baseX, this.baseY);
    }

    this.inventoryWindow.setScreenContainer(this.panel);
    this.inventoryWindow.show("left");
    this.inventoryWindow.setOnAnySlotClick(() => this.openStorage("inventory"));
    const invContainer = (this.inventoryWindow as any).container as Phaser.GameObjects.Container | null;
    if (invContainer) {
      invContainer.setPosition(this.invBaseX, this.baseY);
    }
  }

  refresh(): void {
    this.equipmentWindow.setScreenContainer(this.panel);
    this.equipmentWindow.show();
    const equipContainer = (this.equipmentWindow as any).container as Phaser.GameObjects.Container | null;

    this.inventoryWindow.setScreenContainer(this.panel);
    this.inventoryWindow.show("left");
    this.inventoryWindow.setOnAnySlotClick(() => this.openStorage("inventory"));
    const invContainer = (this.inventoryWindow as any).container as Phaser.GameObjects.Container | null;

    if (this.storageOpen && this.storageOpenMode) {
      const invMode = this.storageOpenMode === "inventory";
      const selectedX = invMode ? this.baseX - 48 : this.baseX;
      const contentW = invMode ? INV_CONTENT_W : EQUIP_CONTENT_W;
      const storageX = selectedX + contentW + WINDOW_GAP;

      this.playerSprite.setX(OFF_SCREEN_LEFT);
      this.weaponSprite.setX(OFF_SCREEN_LEFT);
      this.hatSprite.setX(OFF_SCREEN_LEFT);
      this.pedestalShadow.setX(OFF_SCREEN_LEFT);
      this.pedestal.setX(OFF_SCREEN_LEFT - this.playerX);

      if (equipContainer) equipContainer.setPosition(this.storageOpenMode === "equipment" ? this.baseX : OFF_SCREEN_LEFT, this.baseY);
      if (invContainer) invContainer.setPosition(invMode ? this.baseX - 48 : OFF_SCREEN_LEFT, this.baseY);

      this.storageWindow.setScreenContainer(this.panel);
      this.storageWindow.show(true);
      this.storageWindow.setScrollMaskOffset(storageX, this.baseY);
      const storageContainer = (this.storageWindow as any).container as Phaser.GameObjects.Container | null;
      if (storageContainer) storageContainer.setPosition(storageX, this.baseY);
    } else {
      if (equipContainer) equipContainer.setPosition(this.baseX, this.baseY);
      if (invContainer) invContainer.setPosition(this.invBaseX, this.baseY);
    }

    this.updateReloadAllButton();
  }

  hide(): void {
    this.loadoutWindow.hide();
    (window as any).socket?.off("loadout-items");
    // Kill tweens and snap everything back instantly
    this.scene.tweens.killTweensOf([this.playerSprite, this.weaponSprite, this.hatSprite, this.pedestalShadow, this.pedestal, this.backButton]);
    const equipContainer = (this.equipmentWindow as any).container as Phaser.GameObjects.Container | null;
    const invContainer = (this.inventoryWindow as any).container as Phaser.GameObjects.Container | null;
    const storageContainer = (this.storageWindow as any).container as Phaser.GameObjects.Container | null;
    if (equipContainer) this.scene.tweens.killTweensOf(equipContainer);
    if (invContainer) this.scene.tweens.killTweensOf(invContainer);
    if (storageContainer) this.scene.tweens.killTweensOf(storageContainer);

    this.storageOpen = false;
    this.storageOpenMode = null;
    this.backButton.setX(this.backButtonBaseX);
    this.confirmButton.setVisible(true);
    this.storageWindow.hide();

    this.equipmentWindow.hide();
    this.inventoryWindow.hide();
    this.playerSprite.anims.stop();
    this.playerSprite.setFrame(0);
    this.playerSprite.setX(this.playerX);
    this.weaponSprite.setX(this.playerX);
    this.hatSprite.setX(this.playerX);
    this.hatSprite.y = this.hatBaseY;
    this.pedestalShadow.setX(this.playerX);
    this.pedestal.setPosition(0, 0);
    this.pedestalShadow.setVisible(false);
    this.pedestal.setVisible(false);
    this.playerSprite.setVisible(false);
    this.weaponSprite.setVisible(false);
    this.hatSprite.setVisible(false);
    this.armorBar.hide();
    this.removePlayerNameDisplay();
    this.removeDailyChallengeBox();
    this.backButton.setVisible(false);
    this.confirmButton.setVisible(false);
    this.readyButton.setVisible(false);
    this.reloadAllButton.setVisible(false);
    this.reloadAllCostText.setVisible(false);
    this.squadPartnerDisplay.setScreenVisible(false);
  }
}
